import { FC } from 'react';
import styled from 'styled-components';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
} from '@mui/material';
import { PersonAdd, Close } from '@mui/icons-material';
import { useAddContact } from '@vs-online-only-app/state';
import { useSelectedHeroConversation } from '../../hooks/useSelectedHeroConversation';

type Props = {
  onClose: () => void;
};

const CloseButton = styled(IconButton)`
  position: absolute;
  top: ${({ theme }) => theme.spacing(1.5)};
  right: ${({ theme }) => theme.spacing(1.5)};
`;

export const AddContactConfirmationModal: FC<Props> = ({ onClose }) => {
  const conversation = useSelectedHeroConversation();
  const { mutate: addContact, isLoading, error } = useAddContact();

  const handleConfirm = () => {
    const shopperId = conversation?.conversationStarter?.id;
    if (!shopperId) return;

    addContact(shopperId, {
      onSuccess: () => {
        onClose();
      },
    });
  };

  if (!conversation) {
    return null;
  }

  const shopperName =
    conversation.conversationStarter.firstName || 'this shopper';

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <CloseButton onClick={onClose}>
        <Close />
      </CloseButton>
      <DialogTitle>
        <Stack direction="row" alignItems="center" gap={1}>
          <PersonAdd />
          <Typography fontWeight="bold">Invite {shopperName} to Contacts</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Typography>
          This will send an invite to {shopperName} to be added to your
          contacts. We will let you know when they accept the invitation.
        </Typography>
        {!!error && (
          <Alert severity="error">
            {error instanceof Error ? error.message : 'An error occurred.'}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={isLoading}
          variant="text"
          fullWidth
        >
          Cancel
        </Button>
        <Button
          fullWidth
          onClick={handleConfirm}
          variant="contained"
          disabled={isLoading}
        >
          {isLoading ? <CircularProgress size={20} /> : 'Send Invite'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
